import { useState } from "react";
import { Check, BadgeCheck, MessageCircle, X } from "lucide-react";
import BottomSheet from "@/components/BottomSheet";

const DEFAULT_PLANS = {
  verified: {
    name: "Plan Verificado",
    currencies: ["USD"],
    prices: [
      { label: "6 meses", usd: 60 },
      { label: "12 meses", usd: 72 },
    ],
    features: [
      "4 fotos en el perfil + galería de 4 fotos",
      "Insignia de verificado",
      "Registro Senecyt, ciudad y provincia",
      "Etiqueta de lugar de trabajo (enlaza a la clínica)",
      "Enlace a Instagram",
      "Día de atención (horario)",
      "Botón de WhatsApp",
      "Cobertura de aseguradoras",
      "Mapa de ubicación integrado",
      "Botón para compartir el perfil",
      "Prioridad y mejor posición en resultados",
    ],
  },
  free: {
    name: "Plan Free",
    price_label: "Gratis",
    features: [
      "1 foto de perfil",
      "Registro Senecyt, ciudad y provincia",
      "Lugar de trabajo (texto)",
      "Dirección escrita",
      "Botón de WhatsApp",
    ],
  },
};

export default function PlansSheet({ open, onOpenChange, config }) {
  const plans = config?.plans || DEFAULT_PLANS;
  const verified = plans.verified || DEFAULT_PLANS.verified;
  const free = plans.free || DEFAULT_PLANS.free;
  const prices = verified.prices?.length ? verified.prices : DEFAULT_PLANS.verified.prices;
  const currencies = verified.currencies?.length ? verified.currencies : ["USD"];

  const [priceIdx, setPriceIdx] = useState(prices.length - 1);
  const [currency, setCurrency] = useState(currencies[0]);
  const selected = prices[priceIdx] || prices[0];

  const amountOf = (p) => (currency === "ARS" ? p.ars ?? 0 : p.usd ?? 0);
  const fmtMoney = (val) => {
    const n = Number(val) || 0;
    const dec = Number.isInteger(n) ? 0 : 2;
    return currency === "ARS"
      ? `$${n.toLocaleString("es-AR", { minimumFractionDigits: dec, maximumFractionDigits: 2 })}`
      : `$${n.toFixed(dec)}`;
  };
  const fmt = (p) => fmtMoney(amountOf(p));
  const monthsOf = (p) => {
    const m = String(p.label || "").match(/\d+/);
    return m ? parseInt(m[0], 10) : 1;
  };
  const monthlyOf = (p) => amountOf(p) / (monthsOf(p) || 1);

  const number = (config?.advertise_link || "593999123456").replace(/\D/g, "") || "593999123456";
  const buy = (planName, extra = "") => {
    const msg = `Hola 👋, quiero adquirir el *${planName}* de Odontopick.${extra ? " " + extra : ""}`;
    window.open(`https://wa.me/${number}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  return (
    <BottomSheet open={open} onOpenChange={onOpenChange} className="safe-bottom max-h-[92vh] rounded-t-[34px] bg-white">
      <div data-testid="plans-sheet" className="no-scrollbar overflow-y-auto px-5 pb-8 pt-4">
        <div className="mb-5 flex items-center justify-between">
          <div>
            <h2 className="font-brand text-2xl font-semibold text-white">Elige tu plan</h2>
            <p className="text-sm text-white/70">Aparece en el directorio de Odontopick.</p>
          </div>
          <button
            data-testid="close-plans-btn"
            onClick={() => onOpenChange(false)}
            className="grid h-9 w-9 place-items-center rounded-full bg-white/20 text-white"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Verified plan */}
        <div className="rounded-[26px] bg-neutral-900 p-5 text-white shadow-xl">
          <div className="flex items-center gap-2">
            <BadgeCheck className="h-6 w-6 fill-white text-neutral-900" />
            <h3 className="font-brand text-xl font-semibold">{verified.name || "Plan Verificado"}</h3>
          </div>

          {currencies.length > 1 && (
            <div className="mt-3 inline-flex rounded-full bg-white/10 p-1" data-testid="currency-toggle">
              {currencies.map((cur) => (
                <button
                  key={cur}
                  data-testid={`currency-${cur}`}
                  onClick={() => setCurrency(cur)}
                  className={`rounded-full px-4 py-1.5 text-sm font-semibold ${
                    currency === cur ? "bg-white text-neutral-900" : "text-white/70"
                  }`}
                >
                  {cur === "ARS" ? "Pesos ARS" : "Dólares USD"}
                </button>
              ))}
            </div>
          )}

          {prices.length > 0 && (
            <div className="mt-4 grid grid-cols-2 gap-3">
              {prices.map((p, i) => (
                <button
                  key={i}
                  data-testid={`price-${i}`}
                  onClick={() => setPriceIdx(i)}
                  className={`relative rounded-2xl border p-3 text-left transition-colors ${
                    priceIdx === i ? "border-white bg-white/10" : "border-white/20"
                  }`}
                >
                  {i === prices.length - 1 && (
                    <span className="absolute -top-2 right-2 rounded-full px-2 py-0.5 text-[10px] font-bold text-white shadow" style={{ backgroundColor: "#5757E5" }}>
                      Más solicitado
                    </span>
                  )}
                  <p className="text-sm text-white/70">{p.label}</p>
                  <p className="font-brand text-2xl font-semibold">{fmtMoney(monthlyOf(p))}<span className="text-sm font-normal text-white/70">/mes</span></p>
                  <p className="text-xs text-white/50">{fmt(p)} total + IVA{currency === "ARS" ? " (ARS)" : ""}</p>
                </button>
              ))}
            </div>
          )}

          <ul className="mt-4 space-y-2">
            {verified.features?.map((f) => (
              <li key={f} className="flex items-start gap-2 text-sm text-white/90">
                <Check className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={2.4} style={{ color: "#5757E5" }} />
                {f}
              </li>
            ))}
          </ul>

          <button
            data-testid="buy-verified-btn"
            onClick={() =>
              buy(
                verified.name || "Plan Verificado",
                selected ? `Opción: ${selected.label} — ${fmtMoney(monthlyOf(selected))}/mes (total ${fmt(selected)} ${currency}).` : ""
              )
            }
            className="mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-white py-4 font-semibold text-neutral-900 active:scale-[0.98]"
          >
            <img src="/assets/whatsapp.png" alt="" className="h-5 w-5 shrink-0 object-contain" />
            {selected ? `Adquirir — ${selected.label} · ${fmt(selected)}` : "Adquirir"}
          </button>
        </div>

        {/* Free plan */}
        <div className="mt-4 rounded-[26px] border border-neutral-200 bg-white p-5">
          <h3 className="font-brand text-xl font-semibold text-neutral-900">{free.name || "Plan Free"}</h3>
          <p className="text-sm text-neutral-500">{free.price_label || "Gratis"}</p>
          <ul className="mt-4 space-y-2">
            {free.features?.map((f) => (
              <li key={f} className="flex items-start gap-2 text-sm text-neutral-700">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-neutral-400" strokeWidth={2.4} />
                {f}
              </li>
            ))}
          </ul>
          <button
            data-testid="buy-free-btn"
            onClick={() => buy(free.name || "Plan Free", "Quiero registrarme gratis.")}
            className="mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-neutral-900 py-4 font-semibold text-white active:scale-[0.98]"
          >
            <img src="/assets/whatsapp.png" alt="" className="h-5 w-5 shrink-0 object-contain" style={{ filter: "brightness(0) invert(1)" }} />
            Adquirir {free.name || "plan Free"}
          </button>
        </div>
      </div>
    </BottomSheet>
  );
}
